import { useEffect, useMemo, useState } from 'react'

type UseImageFitOptions = {
  // Thumbnail constraints (when dialog is closed)
  thumbnailMaxWidth?: number // default 450
  thumbnailMaxHeight?: number // optional; if not provided height is unconstrained for thumbnail

  // Dialog viewport paddings (subtracted from window size to get usable space)
  dialogMaxWidthOffset?: number // default 64 (padding/margins)
  dialogMaxHeightOffset?: number // default 140 (header, actions, paddings)
}

export function useImageFit(
  src: string | undefined,
  isDialogOpen: boolean,
  opts: UseImageFitOptions = {}
) {
  const {
    thumbnailMaxWidth = 450,
    thumbnailMaxHeight,
    dialogMaxWidthOffset = 64,
    dialogMaxHeightOffset = 140
  } = opts
  const [natural, setNatural] = useState({ width: 0, height: 0 })
  const [display, setDisplay] = useState({
    width: thumbnailMaxWidth,
    height: 0
  })
  const [imgLoading, setImgLoading] = useState(false)

  // Load natural image dimensions when src changes
  useEffect(() => {
    if (!src) {
      setNatural({ width: 0, height: 0 })
      setDisplay({ width: thumbnailMaxWidth, height: 0 })
      setImgLoading(false)
      return
    }
    let cancelled = false
    setImgLoading(true)
    const img = new Image()
    img.src = src
    img.onload = () => {
      if (!cancelled) {
        const width = (img.naturalWidth || (img as any).width || 0) as number
        const height = (img.naturalHeight || (img as any).height || 0) as number
        setNatural({ width, height })
        setImgLoading(false)
      }
    }
    img.onerror = () => {
      if (!cancelled) {
        setNatural({ width: 0, height: 0 })
        setImgLoading(false)
      }
    }
    return () => {
      cancelled = true
    }
  }, [src, thumbnailMaxWidth])

  // Compute fitted display size based on natural size and container max width
  useEffect(() => {
    const compute = () => {
      const natW = natural.width
      const natH = natural.height

      // Establish constraints based on context (dialog vs thumbnail)
      const maxW = isDialogOpen
        ? Math.max(0, window.innerWidth - dialogMaxWidthOffset)
        : thumbnailMaxWidth
      const maxH = isDialogOpen
        ? Math.max(0, window.innerHeight - dialogMaxHeightOffset)
        : thumbnailMaxHeight ?? Number.POSITIVE_INFINITY

      if (!natW || !natH) {
        // Without natural size we can only set width; height unknown until load
        setDisplay({ width: maxW, height: 0 })
        return
      }

      // Scale to fit within both width and height constraints while preserving aspect ratio
      const scaleW = maxW / natW
      const scaleH = maxH / natH
      const scale = Math.min(scaleW, scaleH, 1)
      const width = Math.round(natW * scale)
      const height = Math.round(natH * scale)
      setDisplay({ width, height })
    }

    compute()
    if (isDialogOpen) {
      const onResize = () => compute()
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }
  }, [
    natural.width,
    natural.height,
    isDialogOpen,
    thumbnailMaxWidth,
    thumbnailMaxHeight,
    dialogMaxWidthOffset,
    dialogMaxHeightOffset
  ])

  const ready = useMemo(() => !!(natural.width && natural.height), [natural])

  return {
    naturalWidth: natural.width,
    naturalHeight: natural.height,
    displayWidth: display.width,
    displayHeight: display.height,
    imgLoading,
    ready
  }
}
